import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePlaceTranslationMap } from "@/hooks/use-place-translations";
import { type Item, type Section, type Tag } from "@/data/places";
import type { CityId } from "@/data/cities";
import { formatEventDateRange } from "@/lib/event-date";
import { hasTfPendingTag } from "@/lib/tf-pending";
import { hasDistrictHubTag } from "@/lib/item-link";


export function dbRowToItem(row: any, extraSlugs?: string[]): Item {
  return {
    dbId: row.id,
    id: row.slug || row.id,
    section: row.section as Section,
    city: row.city_slug as CityId,
    name: row.name,
    category: row.category ?? "",
    categorySlug: row.category_slug ?? undefined,
    categorySlugs: extraSlugs && extraSlugs.length > 0 ? extraSlugs : (row.category_slug ? [row.category_slug] : []),
    area: row.area ?? "",
    address: row.address ?? undefined,
    description: row.description ?? "",
    image: row.image_url ?? "",
    rating: row.rating ?? undefined,
    tags: (row.tags ?? []) as Tag[],
    link: row.link ?? undefined,
    reserveLink: row.reserve_link ?? undefined,
    cta: row.cta ?? undefined,
    // Event visibility already uses these timestamps, so cards must derive their
    // label from the same source instead of a stale manually-entered label.
    date: row.section === "happening"
      ? formatEventDateRange(row.starts_at, row.ends_at) ?? row.date_label ?? undefined
      : row.date_label ?? undefined,
    startsAt: row.starts_at ?? undefined,
    endsAt: row.ends_at ?? undefined,
    price: row.price ?? undefined,
    lat: row.lat ?? undefined,
    lng: row.lng ?? undefined,
    gallery: (row.gallery_urls ?? []) as string[],
    overviewBody: row.overview_body ?? undefined,
    detailsBody: row.details_body ?? undefined,
    ticketsInfo: row.tickets_info ?? undefined,
    scheduleInfo: row.schedule_info ?? undefined,
    showCountdown: row.show_countdown ?? true,
    eventStatusOverride: (row.event_status_override ?? null) as Item["eventStatusOverride"],
    buyTicketUrl: row.buy_ticket_url ?? undefined,
    bokunProductId: row.bokun_product_id ?? undefined,
    region: row.region ?? null,
    hasMenu: Boolean(row.menu_external_url || row.menu_pdf_url || row.menu_image_url),
    menuOnly: row.listing_type === "menu_only",
    openingHours: (row.opening_hours ?? null) as Item["openingHours"],
    specialHours: (row.special_hours ?? null) as Item["specialHours"],
    temporarilyClosed: !!row.temporarily_closed,
  };
}

function usePublicPlacesQuery() {
  const query = useQuery({
    queryKey: ["public-places"],
    queryFn: async () => {
      const [placesRes, joinRes] = await Promise.all([
        supabase
          .from("places")
          .select(
            // Card/list columns only. Long-form body text and gallery arrays
            // are fetched by the place detail route's own loader — including
            // them here made this single request several megabytes.
            "id,slug,section,city_slug,name,category,category_slug,area,address,description,image_url,rating,tags,link,reserve_link,cta,date_label,starts_at,ends_at,price,lat,lng,show_countdown,event_status_override,buy_ticket_url,bokun_product_id,region,listing_type,menu_external_url,menu_pdf_url,menu_image_url,opening_hours,special_hours,temporarily_closed",
          )
          .eq("published", true)
          .order("sort_order", { ascending: true }),
        supabase.from("place_categories").select("place_id,category_slug"),
      ]);
      if (placesRes.error) throw placesRes.error;
      if (joinRes.error) throw joinRes.error;
      const byPlace = new Map<string, string[]>();
      for (const row of joinRes.data ?? []) {
        const arr = byPlace.get((row as any).place_id) ?? [];
        arr.push((row as any).category_slug);
        byPlace.set((row as any).place_id, arr);
      }
      const live = (placesRes.data ?? []).filter(
        (r: any) => !hasTfPendingTag(r.tags) && !hasDistrictHubTag(r.tags),
      );
      return { published: live.map((r: any) => dbRowToItem(r, byPlace.get(r.id))) };
    },
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });

  return query;
}

export function usePublicPlaces(): Item[] {
  const { data } = usePublicPlacesQuery();
  // 100% admin-managed: only DB-published places are shown. No static seed fallback.
  const rows = data?.published ?? [];
  // Overlay stored translations for the active language (no-op in English).
  const translations = usePlaceTranslationMap();
  return useMemo(() => {
    if (Object.keys(translations).length === 0) return rows;
    return rows.map((item) => {
      const t = item.dbId ? translations[item.dbId] : undefined;
      if (!t) return item;
      return {
        ...item,
        name: t.name?.trim() || item.name,
        description: t.description?.trim() || item.description,
        area: t.area?.trim() || item.area,
        category: t.category?.trim() || item.category,
      };
    });
  }, [rows, translations]);
}

/**
 * True while the initial places fetch is in flight and no rows are cached yet.
 * Used to render lightweight skeletons on listing pages.
 */
export function usePublicPlacesLoading(): boolean {
  const { data, isFetching, isPending } = usePublicPlacesQuery();
  const hasRows = (data?.published?.length ?? 0) > 0;
  return (isPending || isFetching) && !hasRows;
}

export function useListedPlaces(): Item[] {
  return usePublicPlaces();
}

